import { globalLoggerToken } from "@/tokens";
import { debounceTime, firstValueFrom, fromEvent, map } from "rxjs";
import { inject, singleton } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import ScrollManagerInterface from "./ScrollManagerInterface";

type ScrollManagerItems = Record<
    string,
    { element: HTMLElement; cb?: () => void }
>;

@singleton()
class ScrollManager implements ScrollManagerInterface {
    private items: ScrollManagerItems = {};

    constructor(
        @inject(globalLoggerToken) private readonly logger: LoggerInterface
    ) {}

    register(name: string, element: HTMLElement, cb?: () => void) {
        if (this.items["name"]) {
            throw new Error("Element with this name is already registered!");
        }

        this.items[name] = { element, cb };

        this.logger.debug(
            `ScrollManager: Element with name ${name} registered`
        );
    }

    unregister(name: string) {
        if (!this.items[name]) {
            throw new Error("Element with this name does not exist!");
        }

        delete this.items[name];
    }

    async scrollTo(name: string) {
        if (!this.items[name]) {
            throw new Error("Element with this name does not exist!");
        }

        const item = this.items[name];
        const isFinished = await this.scrollToElementRef(
            item.element,
            { behavior: "smooth" },
            true
        );
        isFinished && item.cb && item.cb();

        return isFinished;
    }

    private scrollToElementRef(
        element: HTMLElement,
        options?: ScrollIntoViewOptions,
        emitFinish = false
    ): void | Promise<boolean> {
        element.scrollIntoView(options);
        if (emitFinish) {
            return firstValueFrom(
                fromEvent(window, "scroll").pipe(
                    debounceTime(100),
                    map(() => true)
                )
            );
        }
    }

    public getItems(): ScrollManagerItems {
        return this.items;
    }
}

export default ScrollManager;
